import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useMemo, useRef } from 'react';
import { styled } from 'styled-components';
import { border } from 'styled-system';
import { getErrorId, getInfoId } from '@hivebrite/utils';
import { Box } from '@design-system/box';
import { CATEGORIES, FontAwesomeIcon } from '@design-system/font-awesome-icon';
import { Label } from '@design-system/label';
import { useTheme } from '@design-system/theme';
import { BaseInput } from './BaseInput';
const getInputComponent = ({ details, label, tooltip, }) => forwardRef((_a, ref) => {
    var { ariaDescribedBy, ariaLabel, checked, dataTestId, disabled, error, id, info, labelMultiline, name, onChange, required, type } = _a, props = __rest(_a, ["ariaDescribedBy", "ariaLabel", "checked", "dataTestId", "disabled", "error", "id", "info", "labelMultiline", "name", "onChange", "required", "type"]);
    const localRef = useRef(null);
    const inputRef = (ref || localRef);
    const theme = useTheme();
    const hasError = Boolean(error);
    const errorId = useMemo(() => getErrorId(id), [id]);
    const hasInfo = Boolean(info);
    const infoId = useMemo(() => getInfoId(id), [id]);
    return (_jsxs(Box, { alignItems: "flex-start", children: [_jsxs(Wrapper, { "$checked": checked, children: [checked && (_jsx(IconWrapper, { "$checked": checked, children: _jsx(FontAwesomeIcon, { height: "16px", iconName: "check", iconType: CATEGORIES.SOLID }) })), _jsx(Input, Object.assign({}, ((hasError || hasInfo || ariaDescribedBy) && {
                        'aria-describedby': [
                            hasError && errorId,
                            hasInfo && infoId,
                            ariaDescribedBy,
                        ]
                            .filter(Boolean)
                            .join(' '),
                    }), (hasError && {
                        'aria-invalid': hasError,
                    }), (ariaLabel && {
                        'aria-label': ariaLabel,
                    }), { "aria-checked": Boolean(checked), "aria-required": Boolean(required), checked: Boolean(checked), "data-testid": dataTestId, disabled: disabled, id: id, name: name, onChange: onChange, ref: inputRef, type: type }, props))] }), _jsxs(Box, { flexDirection: "column", children: [label && (_jsx(Label, { disabled: disabled, fontWeight: 400, id: id, multiline: labelMultiline, position: "right", required: required, tooltip: tooltip, wrapperProps: { height: `${theme.space[5]}px` }, children: label })), details && (_jsx(DetailsText, { disabled: disabled, onClick: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, children: details }))] })] }));
});
export const CheckboxInput = (_a) => {
    var { details, disabled = false, label, tooltip, value, variant = 'normal', width = '100%' } = _a, props = __rest(_a, ["details", "disabled", "label", "tooltip", "value", "variant", "width"]);
    const inputComponent = useMemo(() => getInputComponent({ label, tooltip, details }), [details, label, tooltip]);
    return (_jsx(BaseInput, Object.assign({ checked: Boolean(value), disabled: disabled, inputComponent: inputComponent, type: "checkbox", variant: variant, width: width }, props)));
};
const Wrapper = styled(Box)
    .withConfig({
    shouldForwardProp: (prop) => !['borderColor', 'borderRadius'].includes(prop),
})
    .attrs(({ $checked, theme }) => ({
    bg: $checked
        ? theme.checkbox.background.checked
        : theme.checkbox.background.unchecked,
    borderColor: theme.checkbox.border.color,
    borderRadius: theme.checkbox.border.radius,
    color: theme.colors.white,
    pl: '2px',
    pt: '1px',
})) `
  border: 1px solid;
  flex-shrink: 0;
  height: ${({ theme }) => theme.space[5]}px;
  position: relative;
  width: ${({ theme }) => theme.space[5]}px;
  ${border}
  ${({ $checked }) => $checked && `z-index: 1;`}
  &:focus-within {
    box-shadow: ${(props) => props.theme.focusOutline.boxShadow};
  }
`;
const IconWrapper = styled.div `
  z-index: -1;
`;
const Input = styled.input `
  cursor: ${({ disabled }) => disabled ? 'defCheckboxInputComponentPropsult' : 'pointer'};
  // To avoid zoom on iOS mobile devices, inputs must have font-size set to 16px (font-size is not really used here, no need to scope to only s & m)
  font-size: ${({ theme }) => theme.fontSizes[3]} !important;
  height: 100%;
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;
const DetailsText = styled(Box).attrs(({ theme }) => ({
    as: 'span',
    ml: theme.label.marginToInput,
    font: 'metadata-regular-dove',
    mt: 2,
})) `
  font-weight: inherit;
  cursor: pointer;
  ${({ disabled }) => disabled && `pointer-events: none;`}
`;
//# sourceMappingURL=CheckboxInput.js.map